<template lang="pug">
div
  v-row(v-if="showControls && !disabled")
    v-col.pb-1(cols="12")
      MenuBar(
        :editor="editor",
        v-if="editor",
        :inline="inline"
        :additional-menu-items="additionalMenuItems"
      )
    transition(name="slide-fade")
      slot(:registerAddon="registerAddon")

    v-col.pt-1(cols="12")
      CoreWysiwyg(:value="value"
        show-dropzone-indication
        :placeholder="placeholder"
        :inline="inline"
        :extensions="extensions"
        :disabled="disabled"
        @editorReady="onEditorReady"
        @input="onInput"
      )

  CoreWysiwyg(v-else
  :value="value"
    show-dropzone-indication
    :placeholder="placeholder"
    :inline="inline"
    :extensions="extensions"
  :disabled="disabled"
    @editorReady="onEditorReady"
    @input="onInput"
  )
</template>

<script lang="ts">
import { defineComponent, getCurrentInstance, inject, provide, Ref } from "vue"
import { EditorContent } from "@tiptap/vue-2"
import { Editor } from "@tiptap/core"
import { PropType, ref } from "vue"
import MenuBar from "./menu/MenuBar.vue"
import { CustomMenuItemObject } from "components/Wysiwyg/menu/MenuItem.vue"
import { WysiwygAddon } from "features/wysiwyg/addon/wysiwyg-addon.types"
import {
  ConfiguredSuusMentionExtension,
  SuggestionFunction,
} from "components/Wysiwyg/extensions/suus-mention/suus-mention.config"
import CoreWysiwyg from "components/Wysiwyg/CoreWysiwyg.vue"
import AttachmentUploader from "features/attachments/AttachmentUploader.vue"
import { Link } from "@tiptap/extension-link"
import AttachmentItemExtension from "components/Wysiwyg/extensions/attachment/attachment-item/attachment-item.extension"
import FileHandler from "@tiptap-pro/extension-file-handler"
import { mdiAttachment } from "@mdi/js"
import { AttachmentMediaUpload } from "features/attachments/attachment.types"
import { isFileSupported } from "app/modules/core/supported-image-formats"

export default defineComponent({
  components: {
    AttachmentUploader,
    CoreWysiwyg,
    EditorContent,
    MenuBar,
  },
  props: {
    value: { type: String, default: "" },
    showControls: { type: Boolean, default: true },
    placeholder: { type: String, default: "" },
    inline: { type: Boolean, default: false }, // note that this prop is not reactive, because it is set during mount
    getMentionables: {
      type: Function as PropType<SuggestionFunction>,
      default: (_) => [],
    },
    disabled: { type: Boolean, default: false },
  },
  setup(props, { emit }) {
    const additionalMenuItems: CustomMenuItemObject[] = []

    provide("retryUpload", (frontendId: string) =>
      emit("retryUpload", frontendId)
    )
    provide("cancelUpload", (frontendId: string) =>
      emit("cancelUpload", frontendId)
    )

    const uploadFile: (file: File) => Ref<AttachmentMediaUpload> =
      inject("uploadFile")

    const registerAddon = (wysiwygAddon: WysiwygAddon) => {
      additionalMenuItems.push(wysiwygAddon.addonMenuItem)
    }

    const editor = ref(null as Editor)

    const updateNodeBasedOnProp = (
      editor: Editor,
      propName: string,
      propValue: any,
      newAttributes: Record<string, any>
    ) => {
      // Clone the current state to ensure we're always working with the latest during iteration
      let updatedState = editor.state

      updatedState.doc.descendants((node, pos) => {
        if (node.attrs[propName] === propValue) {
          const tr = updatedState.tr // Start a new transaction from the updated state
          tr.setNodeMarkup(pos, null, { ...node.attrs, ...newAttributes }) // Prepare to update node attributes

          // Check if the transaction can be applied
          if (tr.docChanged) {
            editor.view.updateState(editor.view.state.apply(tr)) // Apply the transaction
            updatedState = editor.view.state // Update the state reference for the next iteration
          }
        }
      })

      emit("input", editor.getHTML())
    }

    const handleFileDrop = (editor: Editor, files: File[], pos: number) => {
      const { tr } = editor.state // Get the current transaction

      for (let file of files) {
        // skip folders
        if (file.type === "") {
          continue
        }

        // Do not use await here to execute uploads of multiple files in parallel
        const attachmentMediaUpload = uploadFile(file)

        const editorContent = editor.state.doc.content
        const isContentEmpty = editorContent.size === 0 // Check if the editor is empty
        const isContentOnlyEmptyParagraph =
          editorContent.toJSON().length === 1 && // ATTENTION: I dont know why, but editorContent.toJSON().length is 1, editorContent.size would be 2.
          editorContent.firstChild?.type.name === "paragraph" &&
          editorContent.firstChild?.content.size === 0 // Check if the editor contains only a placeholder

        if (
          (pos === 1 || pos === 2) &&
          (isContentEmpty || isContentOnlyEmptyParagraph)
        ) {
          console.warn("Setting position to 0 because editor is empty.")
          pos = 0 // Set position to the beginning if the editor is empty & the position is at the beginning
        }

        if (pos > tr.doc.content.size) {
          pos = tr.doc.content.size // Adjust position to the maximum possible if out of bounds
        }
        const isImage = file.type.startsWith("image/")
        const isSupportedImage = isFileSupported(file)
        tr.insert(
          pos,
          editor.schema.nodes.attachmentItem.create({
            "data-frontend-id": attachmentMediaUpload.value.frontendId,
            "data-file-name": file.name, // presence of file-name indicates unsaved state
            "data-stored-file": "", // empty attribute value indicates uploading state
            ...(isImage && isSupportedImage
              ? {
                  "data-image-mode": "full-width",
                  "data-image-size": "",
                }
              : {}),
          })
        )
        pos++ // Update position for next insertion

        attachmentMediaUpload.value.storedFile.then(
          (storedFile) => {
            updateNodeBasedOnProp(
              editor,
              "data-frontend-id",
              attachmentMediaUpload.value.frontendId,
              {
                "data-stored-file": storedFile,
              }
            )
          },
          (err) => {
            console.error(err)
          }
        )
      }
      // Apply the transaction
      if (tr.steps.length > 0) {
        editor.view.dispatch(tr)
      }
    }

    const onPaste = async (
      editor: Editor,
      files: File[],
      htmlContent: string
    ) => {
      if (files.length > 0) {
        await handleFileDrop(editor, files, editor.state.selection.from)
      }
    }

    additionalMenuItems.push({
      icon: mdiAttachment,
      title: "Datei anhängen",
      action: async () => {
        const input = document.createElement("input")
        input.type = "file"
        input.multiple = true // Allow multiple file selection
        input.onchange = async (e: Event) => {
          const target = e.target as HTMLInputElement
          const files = target.files ? Array.from(target.files) : []
          await handleFileDrop(editor.value as any, files, 0)
        }
        input.click()
      },
    })

    const extensions = [
      FileHandler.configure({ onDrop: handleFileDrop, onPaste }),
      AttachmentItemExtension.configure({ HTMLAttributes: {} }),
      ConfiguredSuusMentionExtension(
        props.getMentionables,
        getCurrentInstance().proxy
      ),
      Link, // a-Tags from mention has higher precedence!
    ]

    const onEditorReady = (_editor: Editor) => {
      editor.value = _editor
    }

    const onInput = (val: string) => {
      emit("input", val)
    }

    return {
      registerAddon,
      additionalMenuItems,
      editor,
      extensions,
      onEditorReady,
      onInput,
    }
  },
})
</script>

<style lang="sass">
/* Slide and fade animation */
.slide-fade-enter-active, .slide-fade-leave-active
  transition: all .3s ease

.slide-fade-enter, .slide-fade-leave-to /* .slide-fade-leave-active in <2.1.8 */
   transform: translateY(-20px)
   opacity: 0
</style>
